import React, { useEffect, useRef, useState } from "react";
import Table from "../../Common/Table";
import usePaymentHistory from "./usePaymentHistory";
import icons from "../../../assets/images/constantIcons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Pagination from "../../Common/Pagination";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useLocation } from "react-router-dom";
import { formatRupees } from "../../Common/ApiCommon/ApiService";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const PaymentHistory = () => {
  const {
    columns,
    currentRows,
    totalRows,
    currentPage,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    selectedFilter,
    setSelectedFilter,
    setFromDate,
    setToDate,
    fromDate,
    toDate, data,
  } = usePaymentHistory();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCustomDateOpen, setIsCustomDateOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const amount = location.state?.amount || 0;
  const full_name = location.state?.full_name;

  const filters = ["Today", "Weekly", "Monthly"];

  const handleFilterChange = (filter) => {
    if (filter === "Custom Date") {
      setIsCustomDateOpen((prev) => !prev);
    } else {
      setSelectedFilter(filter.toLowerCase());
      setIsDropdownOpen(false);
    }
  };

  const applyFilter = () => {
    if (fromDate && toDate) {
      setSelectedFilter("custom");
    }
    setIsDropdownOpen(false);
    setIsCustomDateOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setIsCustomDateOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  
  const downloadTransactions = async () => {
    if (data.length === 0) {
      toast.warn("No transactions available to download.", { duration: 1000 });
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Payment History");

    const headers = columns.map((col) => {
      let headerText = "";
      if (typeof col.Header === "string") {
        headerText = col.Header;
      } else if (typeof col.Header === "function") {
        if (col.accessor === "booking_amount") headerText = "Today Amt";
        else if (col.accessor === "total_amount") headerText = "Total Amt";
        else headerText = "";
      }
      return {
        header: headerText.toUpperCase(),
        key: col.accessor || col.id || headerText.toLowerCase(),
        width: 20,
      };
    }).filter(col => col.header);

    worksheet.columns = headers;

    data.forEach((row) => {
      const formattedRow = {};
      headers.forEach(({ key }) => {
        formattedRow[key] = row?.[key] ?? "";
      });
      const addedRow = worksheet.addRow(formattedRow);

      addedRow.eachCell((cell) => {
        cell.alignment = { horizontal: "center", vertical: "middle" };
      });
    });

    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    });

    worksheet.eachRow({ includeEmpty: false }, (row) => {
      row.height = 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const now = new Date();
    const date = now.toLocaleDateString("en-GB").replace(/\//g, "-");
    const time = now
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/:/g, "-")
      .replace(/\s/g, "");

    const fileName = `PaymentHistory_${date}_${time}.xlsx`;
    saveAs(blob, fileName);
  };

  return (
    <div className="relative">
      <div className="px-4 py-4 flex flex-col ">
        <h1 className="main_heading text-lg sm:text-[22px] flex gap-3 items-center mb-3">
          <icons.IoMdArrowRoundBack
            onClick={() => navigate(-1)}
            className=" cursor-pointer text-green text-2xl font-extrabold"
          />
          <p>Payment History</p>
        </h1>
        <div className="flex flex-col xs:flex-row justify-between ">
          <div className="flex flex-col ">
            <h1 className="ml-9 font-medium">Player : {full_name}</h1>
            <h1 className="ml-9 font-medium">
              Balance : {formatRupees(amount)}
            </h1>
          </div>
          {/* Dropdown */}
          <div className="flex items-center justify-end gap-5 mt-2 xs:mt-0">
            <div
              onClick={downloadTransactions}
              className="bg-white text-green rounded-md shadow-sm text-base sm:text-2xl p-2 cursor-pointer"
            >
              <icons.FiDownload />
            </div>
            <div className="relative" ref={dropdownRef}>
              <div
                className="flex border items-center text-G100 p-2 rounded-lg font-normal bg-white cursor-pointer"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <icons.GoSearch className="mr-2" />
                <span className="lg:w-[200px] text-sm sm:text-base">
                  {selectedFilter
                    ? selectedFilter.charAt(0).toUpperCase() +
                    selectedFilter.slice(1).toLowerCase()
                    : "Date period search"}
                </span>
                <icons.RiArrowDropDownLine className="ml-2 text-2xl" />
              </div>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white border rounded-lg shadow-lg z-10 p-2">
                  {filters.map((filter) => (
                    <div
                      key={filter}
                      className={`text-sm p-2 rounded-md cursor-pointer hover:bg-green50 ${selectedFilter === filter.toLowerCase()
                        ? "text-green font-semibold"
                        : "text-G500 "
                        }`}
                      onClick={() => handleFilterChange(filter)}
                    >
                      {filter}
                    </div>
                  ))}

                  {/* Custom Date Picker */}
                  <div
                    className="flex justify-between items-center px-2 py-2 hover:bg-green50 rounded-md cursor-pointer"
                    onClick={() => handleFilterChange("Custom Date")}
                  >
                    <span className=" text-sm text-G500 ">Custom Date</span>
                    <icons.MdOutlineCalendarMonth className="text-green700 text-xl" />
                  </div>
                  {isCustomDateOpen && (
                    <div className="p-2">
                      <DatePicker
                        selected={fromDate}
                        onChange={(date) => setFromDate(date)}
                        selectsStart
                        startDate={fromDate}
                        endDate={toDate}
                        placeholderText="From Date"
                        className="border px-3 py-1 rounded w-full mb-2 outline-none text-sm"
                        dateFormat="yyyy-MM-dd"
                      />
                      <DatePicker
                        selected={toDate}
                        onChange={(date) => setToDate(date)}
                        selectsEnd
                        startDate={fromDate}
                        endDate={toDate}
                        minDate={fromDate}
                        placeholderText="To Date"
                        className="border px-3 py-1 rounded w-full outline-none text-sm"
                        dateFormat="yyyy-MM-dd"
                      />

                      {/* Buttons */}
                      <div className="flex justify-between mt-2">
                        <button
                          className="border border-green text-green text-sm px-4 py-1 rounded-md w-[95px]"
                          onClick={() => {
                            setIsDropdownOpen(false);
                            setIsCustomDateOpen(false);
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={applyFilter}
                          className="border border-green text-green text-sm px-4 py-1 rounded-md w-[95px]"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                  <div
                    onClick={() => {
                      setSelectedFilter("");
                      setFromDate(null);
                      setToDate(null);
                      setIsDropdownOpen(false);
                      setIsCustomDateOpen(false);
                    }}
                    className="w-full text-sm p-2 rounded-md cursor-pointer hover:bg-green50 text-G500 "
                  >
                    Clear filters
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="my-1 mb-6 overflow-auto horizontalscroll">
          <Table columns={columns} data={currentRows} />
        </div>
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalRows={totalRows}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default PaymentHistory;
